<template>
    <div class="data-table">

        <div class="data-table__header">
            <div class="data-table__search">
                <input class="data-table__grow" placeholder="Search" @keyup.enter="search" @input="searchInput($event)"
                    v-model="searchText" autofocus>
                <button :disabled="searchText === null" @click="clearSearch"><font-awesome-icon icon="times"
                        fixed-width></font-awesome-icon></button>
                <button :disabled="searchText === null || searchText === searchQuery" @click="search"><font-awesome-icon
                        icon="search" fixed-width></font-awesome-icon></button>
            </div>
            <data-table-actions>
                <slot name="actions"
                    :entity-name="entityName"
                    :entity-name-plural="entityNamePlural"
                    :entity-name-for-count="entityNameForCount"
                    :select-or-filter="selectOrFilter"
                    :selection-count="selectionCount"
                    :visible-ordered-columns="visibleOrderedColumns"
                    :cell-format="cellFormat"
                    :disabled="actionInProgress || selectionCount === 0 || selectionCount > 100000"
                >
                </slot>
            </data-table-actions>
        </div>

        <div class="data-table__header">
            <div class="data-table__refresh">
                <div class="data-table__header-section">
                    <auto-refresh :poll-interval="pollInterval" :poll-timeout-interval="pollTimeoutInterval"
                        @refresh="refreshRows"></auto-refresh>
                </div>
                <div class="data-table__header-section">
                    <span class="data-table__grow">{{ totalRowCount }} {{ totalRowCount === 1 ? 'record' : 'records' }}
                        {{ isFiltered ? ' (filtered)' : '' }}</span>
                    <a v-if="isFiltered" href="" @click.prevent="clearFilters">Clear filters</a>
                    <span>|</span>
                </div>

                <div class="data-table__header-section">
                    <span>{{ selectionCount }} selected</span>
                    <a v-if="pageSelected && !allSelected" href="" @click.prevent="selectAll">Select all</a>
                    <a v-if="rowSelection.length > 0" href="" @click.prevent="selectNone">Clear selection</a>
                </div>
            </div>

            <div class="data-table__header-section">
                <pagination :currentPage="currentPage" :perPage="perPage" :rowCount="totalRowCount"
                    @pageChanged="pageChanged" @perPageChanged="perPageChanged" :disabled="totalRowCount <= perPage">
                </pagination>
                <column-visibility :columns="orderedColumns" :visible="columnVisibility"
                    @visibleChanged="setColumnVisibility" @orderChanged="moveColumn"></column-visibility>
            </div>
        </div>

        <div class="data-table__table-wrapper">
            <div v-if="isLoading || actionInProgress" class="data-table__spinner">
                <div class="data-table__spinner-icon"></div>
            </div>
            <table class="data-table__table">
                <thead>
                    <tr>
                        <th class="data-table__cell-select" @click="toggleSelectPage">
                            <input type="checkbox" :checked="allSelected || pageSelected" :disabled="rows.length === 0">
                        </th>
                        <template v-for="column in visibleOrderedColumns">
                            <th draggable @dragstart="columnDrag($event, column.key)"
                                @drop="columnDrop($event, column.key)"
                                @dragenter.prevent="columnDragEnter($event, column.key)" @dragover.prevent>
                                <div class="data-table__column-header">
                                    <div class="data-table__column-header__label">
                                        <slot :name="`header(${column.key})`" v-bind:column="column">
                                            {{ column.label }}
                                        </slot>
                                    </div>
                                    <sort-button v-if="canSortColumn(column)" :sort="columnSort[column.proxy_key]"
                                        :column-id="column.proxy_key" @sortChanged="sortColumn"></sort-button>
                                    <column-filter v-if="canFilterColumn(column)"
                                        :filter="Object.hasOwn(columnFilter, column.proxy_key) ? columnFilter[column.proxy_key] : null"
                                        :column-id="column.proxy_key" :type="column.type" :categories="column.categories" :units="column.units"
                                        @filterChanged="filterColumn"></column-filter>
                                </div>
                            </th>
                        </template>
                    </tr>
                </thead>
                <tbody v-if="rows.length === 0" class="data-table__no-data">
                    <tr>
                        <!-- +1 for the selection column -->
                        <td :colspan="visibleOrderedColumns.length + 1">{{ noRecordsText }}</td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <template v-for="row in rows">
                        <tr>
                            <td class="data-table__cell-select" @click="toggleSelectRow(row[selectionKey])"><input
                                    type="checkbox" :checked="allSelected || rowSelection.includes(row[selectionKey])">
                            </td>
                            <td v-for="column in visibleOrderedColumns" :class="`data-table__cell-${column.type}`">
                                <slot v-if="column.type === 'table'" :name="`cell(${column.key})`" v-bind:row="row">
                                    <button @click="toggleExpandRow(row[selectionKey], column)"
                                        :disabled="Object.hasOwn(column, 'disabled') ? column.disabled(row) : row[column.key].length === 0">
                                        {{ row[column.key].length }}
                                        <font-awesome-icon
                                            :icon="row[selectionKey] === expandedRow && column.key === expandedColumn.key ? 'sort-up' : 'sort-down'">
                                        </font-awesome-icon>
                                    </button>
                                </slot>
                                <slot v-else-if="column.type === 'action'" :name="`cell(${column.key})`"
                                    v-bind:row="row">
                                    <button @click="column.action(row)"
                                        :disabled="Object.hasOwn(column, 'disabled') && column.disabled(row)">
                                        <font-awesome-icon :icon="column.actionIcon"></font-awesome-icon>
                                    </button>
                                </slot>
                                <slot v-else :name="`cell(${column.key})`" v-bind:row="row">
                                    {{ cellFormat(column, row) }}
                                </slot>
                            </td>
                        </tr>
                        <tr v-if="row[selectionKey] === expandedRow" id="expanded-tr">
                            <td :colspan="visibleOrderedColumns.length + 1">
                                <div class="data-table__sub-table-wrapper">
                                    <table class="data-table__sub-table">
                                        <tr>
                                            <th class="data-table__sub-table-heading"
                                                :rowspan="row[expandedColumn.key].length + 1">{{ expandedColumn.label }}
                                                <button v-if="expandedColumn.editable"
                                                    @click="showEditModal(expandedColumn.label, row[selectionKey], row[expandedColumn.key], expandedColumn.editable)">
                                                    <font-awesome-icon icon="edit" fixed-width size="xs"></font-awesome-icon>
                                                </button>
                                            </th>
                                            <th v-for="childColumn in expandedColumn.columns">
                                                {{ childColumn.label }}
                                            </th>
                                        </tr>
                                        <tr v-for="childRow in row[expandedColumn.key]">
                                            <td v-for="childColumn in expandedColumn.columns"
                                                :class="`data-table__cell-${childColumn.type}`">

                                                <slot v-if="childColumn.type === 'action'"
                                                    :name="`cell(${childColumn.key})`" v-bind:row="childRow">
                                                    <button @click="childColumn.action(childRow)"
                                                        :disabled="Object.hasOwn(childColumn, 'disabled') && childColumn.disabled(childRow)">
                                                        <font-awesome-icon :icon="childColumn.actionIcon"></font-awesome-icon>
                                                    </button>
                                                </slot>
                                                <slot v-else :name="`cell(${childColumn.key})`" v-bind:row="childRow">
                                                    {{ cellFormat(childColumn, childRow) }}
                                                </slot>
                                            </td>
                                        </tr>
                                    </table>
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <div class="data-table__footer">
        </div>
        <teleport to="body">
            <modal v-if="isEditing" @reject="isEditing = null" @accept="saveEditModal" acceptText="Save" :title="`Edit ${editingRowLabel}`">
                 <table>
                    <tbody>
                        <tr v-for="(row, rowIndex) in editData" :key="rowIndex">
                            <td v-for="(value, key) in row" :key="key">
                                <input class="data-table_cell-input" v-model="row[key]" />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </modal>
        </teleport>
    </div>
</template>
<script>
import AutoRefresh from './AutoRefresh.vue';
import ColumnFilter from './ColumnFilter.vue';
import ColumnVisibility from './ColumnVisibility.vue';
import DataTableActions from './DataTableActions.vue';
import Modal from './Modal.vue';
import Pagination from './Pagination.vue';
import SortButton from './SortButton.vue';
import Teleport from 'vue2-teleport';

export default {
    name: 'DataTable',
    components: {
        AutoRefresh,
        ColumnFilter,
        ColumnVisibility,
        DataTableActions,
        Modal,
        Pagination,
        SortButton,
        Teleport,
    },
    props: {
        entityName: String,
        entityNamePlural: String,
        columns: Array,
        rows: Array,
        totalRowCount: Number,
        selectionKey: String,
        pollInterval: { type: Number, default: 60 },
        pollTimeoutInterval: { type: Number, default: 30 * 60 },
        actionInProgress: Boolean,
    },
    data() {
        return {
            expandedRow: null,
            expandedColumn: null,
            columnMap: null,
            columnOrder: [],
            columnVisibility: {},
            columnSort: {},
            columnFilter: {},
            searchText: null,
            searchQuery: null,
            rowSelection: [],
            allSelected: false,
            currentPage: 1,
            perPage: 10,
            columnClone: null,
            isLoading: false,
            isEditing: null,
            editData: null,
            editingRowId: null,
            editingRowLabel: null,
        }
    },
    methods: {
        cellFormat(column, row) {
            const value = column.key.split('.').reduce((o, i) => o ? o[i] : null, row);

            if (column.formatter) {
                return column.formatter(value);
            }
            if (value === undefined || value === null) return '';

            if (column.type === 'boolean') return value ? '✓' : '✕';
            if (column.type === 'datetime') {
                const d = this.$options.filters.prettyDateTime(value);
                return d;
            }
            return value;
        },
        refreshRows() {
            this.$emit('queryChanged', this.queryParams);
            this.isLoading = true;
        },
        onRowsRefreshed() {
            this.selectNone();
            this.isLoading = false;
        },
        /* Local storage */
        loadFromLocalStorage(settings) {
            // Column order
            if (Object.hasOwn(settings, 'columnOrder') && settings.columnOrder.length > 0) {
                // Use the order from settings, ignore columns that aren't in this.columnOrder.
                let newOrder = [];
                for (let i = 0; i < settings.columnOrder.length; ++i) {
                    if (this.columnOrder.includes(settings.columnOrder[i])) {
                        newOrder.push(settings.columnOrder[i]);
                    }
                }
                // Add columns that are in this.columnOrder but not in settings.columnOrder new ones.
                let extras = [];
                for (let i = 0; i < this.columnOrder.length; ++i) {
                    if (!newOrder.includes(this.columnOrder[i])) {
                        extras.push(this.columnOrder[i]);
                    }
                }
                this.columnOrder = extras.concat(newOrder);
            }

            // Column visibility
            if (Object.hasOwn(settings, 'columnVisibility')) {
                // Use all the entries from this.columnVisibility, override with values from settings.
                let merged = {};
                for (const key of Object.keys(this.columnVisibility)) {
                    if (Object.hasOwn(settings.columnVisibility, key)) {
                        merged[key] = settings.columnVisibility[key];
                    }
                    else {
                        merged[key] = this.columnVisibility[key];
                    }
                }
                this.columnVisibility = merged;
            }


            const proxyMap = {};
            Object.keys(this.columnMap).forEach(e =>
                proxyMap[this.columnMap[e].proxy_key] = e
            );

            // Column sort
            let sanitizedSort = {};
            if (Object.hasOwn(settings, 'columnSort') && settings.columnSort) {
                // Only keep sorts for columns that exist and are visible.
                for (const key of Object.keys(settings.columnSort)) {
                    if (Object.hasOwn(this.columnMap, key)) {
                        if (this.columnVisibility[key]) {
                            sanitizedSort[key] = settings.columnSort[key];
                        }
                    }
                    else if (Object.hasOwn(proxyMap, key)) {
                        if (this.columnVisibility[proxyMap[key]]) {
                            sanitizedSort[key] = settings.columnSort[key];
                        }
                    }
                }
            }
            this.columnSort = sanitizedSort;

            // Column filter
            let sanitizedFilter = {};
            if (Object.hasOwn(settings, 'columnFilter') && settings.columnFilter) {
                // Only keep filters for columns that exist and are visible.
                for (const key of Object.keys(settings.columnFilter)) {
                    if (Object.hasOwn(this.columnMap, key)) {
                        if (this.columnVisibility[key]) {
                            sanitizedFilter[key] = settings.columnFilter[key];
                        }
                    }
                    else if (Object.hasOwn(proxyMap, key)) {
                        if (this.columnVisibility[proxyMap[key]]) {
                            sanitizedFilter[key] = settings.columnFilter[key];
                        }
                    }
                }
            }
            this.columnFilter = sanitizedFilter;

            // Search
            if (Object.hasOwn(settings, 'searchQuery') && settings.searchQuery && settings.searchQuery.length > 0) {
                this.searchText = settings.searchQuery;
                this.searchQuery = settings.searchQuery;
            }

            // Per page
            if (Object.hasOwn(settings, 'perPage')) {
                const allowed = [10, 50, 100];
                if (allowed.includes(settings.perPage)) {
                    this.perPage = settings.perPage;
                }
            }
        },
        saveToLocalStorage() {
            let settings = {
                columnOrder: this.columnOrder,
                columnVisibility: this.columnVisibility,
                columnSort: this.columnSort,
                columnFilter: this.columnFilter,
                searchQuery: this.searchQuery,
                perPage: this.perPage,
            };
            localStorage.setItem(this.entityName, JSON.stringify(settings));
        },

        /* Column arrangement and Sorting */
        columnDrag(event, keyFrom) {
            // Make sure any previous clones are cleaned up (the columnDrop event is not reliable)
            if (this.columnClone !== null) {
                this.columnClone.remove();
                this.columnClone = null;
            }

            // Create a table to contain the cloned column
            this.columnClone = document.createElement('table');
            const srcStyle = document.defaultView.getComputedStyle(event.target);
            this.columnClone.style.width = srcStyle.getPropertyValue('width');

            // Clone the header
            const thead = document.createElement('thead');
            const th = event.target.cloneNode(true);
            thead.appendChild(th);
            this.columnClone.appendChild(thead);

            // Clone at most the first ten rows
            const tbody = document.createElement('tbody');
            // Column index is +1 for 1-based indexing and +1 to skip over the 'select' column
            const columnIndex = this.visibleOrderedColumns.map(e => e.key).indexOf(keyFrom) + 2;
            const tds = document.querySelectorAll(`.data-table__table tr td:nth-child(${columnIndex})`);
            const maxHeight = 1500;
            for (let i = 0; i < tds.length; ++i) {
                if (tds[i].getBoundingClientRect().bottom < 1500) {
                    const tr = document.createElement('tr');
                    tr.appendChild(tds[i].cloneNode(true));
                    tbody.appendChild(tr);
                }
                else {
                    break;
                }
            }
            this.columnClone.appendChild(tbody);

            // Hide the clone off screen before adding to the document body
            this.columnClone.style.position = 'absolute';
            this.columnClone.style.top = `${-maxHeight}px`;
            document.body.appendChild(this.columnClone);

            event.dataTransfer.dropEffect = 'move';
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData(JSON.stringify(keyFrom), '');
            event.dataTransfer.setDragImage(this.columnClone, event.offsetX, event.offsetY);
        },
        columnDragEnter(event, keyOver) {
            // if (!Object.hasOwn(this.columnOrder, event.dataTransfer.types[0]) return false;
            const keyFrom = JSON.parse(event.dataTransfer.types[0]);
            this.moveColumn(keyFrom, keyOver);
        },
        columnDrop(event, keyTo) {
            this.columnClone.remove();
            this.columnClone = null;
        },
        moveColumn(keyFrom, keyTo) {
            if (keyFrom === keyTo) return;
            const indexFrom = this.columnOrder.indexOf(keyFrom);
            const indexTo = this.columnOrder.indexOf(keyTo);
            const column = this.columnOrder[indexFrom];
            this.columnOrder.splice(indexFrom, 1);
            this.columnOrder.splice(indexTo, 0, column);
        },
        sortColumn(key, direction, additive) {
            if (additive) {
                if (direction === null) {
                    this.$delete(this.columnSort, key);
                }
                else {
                    this.$set(this.columnSort, key, direction);
                }
            }
            else {
                if (direction === null) {
                    this.columnSort = {};
                }
                else {
                    this.columnSort = {};
                    this.$set(this.columnSort, key, direction);
                }
            }
        },
        setColumnVisibility(column, visible) {
            // Remove sorts and filters from a hidden column
            if (!visible) {
                this.$delete(this.columnSort, column.proxy_key);
                this.$delete(this.columnFilter, column.proxy_key);
            }
            this.$set(this.columnVisibility, column.key, visible);
        },
        canSortColumn(column) {
            if (Object.hasOwn(column, 'sortable')) {
                return column.sortable;
            }
            else {
                return true;
            }
        },

        /* Search and Filter */
        search() {
            this.searchQuery = this.searchText;
        },
        clearSearch() {
            this.searchText = null;
            this.search();
        },
        searchInput(event) {
            if (event.target.value === '') {
                this.clearSearch();
            }
        },
        filterColumn(key, filters) {
            if (filters.length === 0) {
                this.$delete(this.columnFilter, key);
            }
            else {
                this.$set(this.columnFilter, key, filters);
            }
        },
        canFilterColumn(column) {
            if (Object.hasOwn(column, 'filterable')) {
                return column.filterable;
            }
            else {
                return true;
            }
        },
        clearFilters() {
            this.columnFilter = {};
            this.searchText = null;
            this.search();
        },

        /* Expand row */
        async toggleExpandRow(rowId, column) {
            if (this.expandedRow === rowId && this.expandedColumn.key === column.key) {
                // Collapse the currently expanded row
                this.expandedRow = null;
                this.expandedColumn = null;
                await this.$nextTick(() => {
                    document.querySelectorAll('tr').forEach(el => el.classList.remove('subdued'));
                });
            }
            else if (this.expandedRow === rowId && this.expandedColumn.key !== column.key) {
                // Keep the same row, only the column changed
                this.expandedColumn = column;
                await this.$nextTick(() => {
                    const subTable = document.querySelector('.data-table__sub-table');
                    subTable.scrollIntoViewIfNeeded();
                });
            }
            else {
                // Expand a new row
                this.expandedRow = rowId;
                this.expandedColumn = column;

                await this.$nextTick(() => {
                    document.querySelectorAll('tr').forEach(el => el.classList.remove('subdued'));
                    document.querySelectorAll('tbody tr:not(#expanded-tr)').forEach(el => el.classList.add('subdued'));

                    let expanded = document.querySelector('#expanded-tr');
                    expanded.previousElementSibling.classList.remove('subdued');
                    expanded.querySelectorAll('tr').forEach(el => el.classList.remove('subdued'));
                    const subTable = document.querySelector('.data-table__sub-table');
                    subTable.scrollIntoViewIfNeeded();
                });
            }
        },

        /* Editable fields */
        showEditModal(rowLabel, rowId, data, callback) {
            this.editingRowId = rowId;
            this.editingRowLabel = rowLabel;
            this.editData = _.cloneDeep(data);
            this.isEditing = callback;
        },
        saveEditModal() {
            this.isEditing(this.editingRowId, this.editData);
            this.isEditing = null;
            this.editData = null;
            this.editingRowLabel = null;
            this.editingRowId = null;
        },

        /* Selection */
        toggleSelectRow(key) {
            if (this.rowSelection.includes(key)) {
                this.rowSelection.splice(this.rowSelection.indexOf(key), 1);
            }
            else {
                this.rowSelection.push(key);
            }
            this.allSelected = (this.rowSelection.length === this.totalRowCount);
        },
        toggleSelectPage() {
            if (this.pageSelected) {
                this.rowSelection.splice(0);
            }
            else {
                this.rowSelection = this.rows.map(value => value[this.selectionKey]);
            }
            this.allSelected = (this.rowSelection.length === this.totalRowCount);
        },
        selectNone() {
            this.allSelected = false;
            this.rowSelection.splice(0);
        },
        selectAll() {
            this.allSelected = true;
            this.rowSelection = this.rows.map(value => value[this.selectionKey]);
        },

        /* Pagination */
        perPageChanged(perPage) {
            this.perPage = perPage;
        },
        pageChanged(page) {
            this.currentPage = page;
        },
    },
    computed: {
        noRecordsText() {
            if (this.isLoading) return 'Loading...';
            return 'No records to display';
        },
        entityNameForCount() {
            return this.selectionCount === 1 ? this.entityName : this.entityNamePlural;
        },
        selectionCount() {
            return this.allSelected ? this.totalRowCount : this.rowSelection.length;
        },
        pageSelected() {
            if (this.rows.length === 0 || this.rowSelection.length < this.rows.length) return false;
            // This is O(n^2) or rather, worst-case when pageSelected is true: O(perPage * (perPage / 2))
            const pageRows = this.rows.map(value => value[this.selectionKey]);
            return pageRows.every(e => this.rowSelection.includes(e));
        },
        isFiltered() {
            return this.searchQuery !== null || Object.keys(this.columnFilter).length > 0;
        },
        orderedColumns() {
            return this.columnOrder.map(e => this.columnMap[e]);
        },
        visibleOrderedColumns() {
            return this.columnOrder.filter(e => this.columnVisibility[e]).map(e => this.columnMap[e]);
        },
        queryParams() {
            const params = new URLSearchParams();
            /* Pagination */
            params.append('skip', (this.currentPage - 1) * this.perPage);
            params.append('limit', this.perPage);

            /* Sort */
            for (const k in this.columnSort) {
                params.append('sort', `${k}__${this.columnSort[k]}`);
            }

            /* Filter */
            for (const k in this.columnFilter) {
                for (const j of this.columnFilter[k]) {
                    params.append(`${k}__${j.operator}`, j.value);
                }
            }

            /* Search */
            if (this.searchQuery) params.append('search', this.searchQuery);

            return params;
        },
        selectOrFilter() {
            if (this.rowSelection.length > 0 && !this.allSelected) {
                let params = {
                    selection: this.rowSelection,
                    sort: [],
                };
                for (const k in this.columnSort) {
                    params.sort.push(`${k}__${this.columnSort[k]}`);
                }
                return params;
            }
            else {
                let params = {
                    sort: [],
                    search: '',
                    filter: {},
                };
                /* Sort */
                for (const k in this.columnSort) {
                    params.sort.push(`${k}__${this.columnSort[k]}`);
                }

                /* Filter */
                for (const k in this.columnFilter) {
                    for (const j of this.columnFilter[k]) {
                        params.filter[`${k}__${j.operator}`] = j.value;
                    }
                }

                /* Search */
                if (this.searchQuery) params.search = this.searchQuery;
                return params;
            }
        },
    },
    watch: {
        columns: {
            handler(newValue) {
                const makeLabel = function (value) {
                    let label = value.replaceAll('_', ' ');
                    return label.charAt(0).toUpperCase() + label.substring(1);
                };
                const defaultColumn = {
                    type: 'text',
                    visible: false,
                    sortable: true,
                    filterable: true,
                }
                const defaultChildColumn = {
                    type: 'text',
                    visible: true,
                    sortable: false,
                    filterable: false,
                }
                this.columnMap = newValue.reduce(function (map, column) {
                    map[column.key] = { ...defaultColumn, ...column };

                    // Generate a label from the key if there isn't one
                    if (!Object.hasOwn(column, 'label')) {
                        map[column.key].label = makeLabel(column.key);
                    }

                    // Use key for sorting if proxy_key not specified
                    if (!Object.hasOwn(column, 'proxy_key')) {
                        map[column.key].proxy_key = column.key;
                    }

                    // Actions cannot be filtered or sorted
                    if (map[column.key].type === 'action') {
                        map[column.key].sortable = false;
                        map[column.key].filterable = false;
                    }

                    // Process any 'sub-table' types
                    if (map[column.key].type === 'table' && !Object.hasOwn(map[column.key], 'editable')) {
                        map[column.key].editable = null;
                    }

                    if (map[column.key].type === 'table' && Object.hasOwn(map[column.key], 'columns')) {
                        let updatedChildColumns = [];
                        for (let childColumn of map[column.key].columns) {
                            childColumn = { ...defaultChildColumn, ...childColumn };
                            if (!Object.hasOwn(childColumn, 'label')) {
                                childColumn.label = makeLabel(childColumn.key);
                            }
                            updatedChildColumns.push(childColumn);
                        }
                        map[column.key].columns = updatedChildColumns;
                        // Not sortable or filterable
                        map[column.key].sortable = false;
                        map[column.key].filterable = false;
                    }
                    else {
                        map[column.key].columns = [];
                    }
                    return map;
                }, {});

                this.columnVisibility = newValue.reduce(function (map, obj) {
                    if (Object.hasOwn(obj, 'visible')) {
                        map[obj.key] = obj.visible;
                    }
                    else {
                        map[obj.key] = false;
                    }
                    return map;
                }, {});
                this.columnOrder = Object.keys(this.columnMap);

                // Load settings from local storage to override defaults and add
                // any sorts, filters, etc. that were saved. Because loadFromLocalStorage
                // modifies sorts and filters it causes queryParams to be recalculated.
                // And we watch queryParams to trigger a refreshRows.
                //
                // If there are no settings explicitly refresh the rows now.
                const settings = JSON.parse(localStorage.getItem(this.entityName));
                if (settings === null) {
                    this.refreshRows();
                }
                else {
                    this.loadFromLocalStorage(settings);
                }
            }
        },
        rows: function () {
            this.onRowsRefreshed();
        },
        queryParams: function () {
            this.refreshRows();
        },
        columnOrder: function () {
            this.saveToLocalStorage();
        },
        columnFilter: function () {
            this.saveToLocalStorage();
        },
        columnSort: function () {
            this.saveToLocalStorage();
        },
        columnVisibility: {
            handler () {
                this.saveToLocalStorage();
            },
            deep: true
        },
        searchQuery: function () {
            this.saveToLocalStorage();
        },
        perPage: function () {
            this.saveToLocalStorage();
        },
    }
}
</script>
<style scoped>
.data-table__table th {
    background-color: buttonface;
    /* Borders don't stick so fake some with a box shadow */
    box-shadow: inset 0.5px 0.5px rgb(200, 202, 203, 0.25), inset 0 -0.5px rgb(200, 202, 203, 0.5);
    font-family: Roboto;
    font-size: smaller !important;
    line-height: 2;
    position: sticky;
    top: 0;
    z-index: 10;
}

.data-table__table td {
    border: 1px solid rgb(200, 202, 203, 0.25);
    font-family: Roboto;
    padding-top: 2px;
    padding-bottom: 2px;
}

.data-table__table th:first-of-type {
    border-right: 1px solid rgb(200, 202, 203, 0.75);
}

.data-table__table td:first-of-type {
    border-right: 1px solid rgb(200, 202, 203, 0.75);
}

.data-table__sub-table #expanded-tr td:first-of-type,
#expanded-tr td:last-of-type {
    border-right: none;
}

.data-table {
    width: 100%;
}

.data-table__table-wrapper {
    overflow-x: auto;
    margin-top: -1px;
    height: 60vh;
    border: 1px solid rgb(118, 118, 118, 0.5);
    border-radius: 2px;
}

.data-table__table {
    table-layout: auto;
    width: 100%;
}

.data-table__sub-table-wrapper {
    margin-top: 8px;
    margin-bottom: 24px;
    margin-left: auto;
    margin-right: auto;
    border: 1px solid rgb(118, 118, 118, 0.5);
    border-radius: 2px;
    width: 72vw;
}

.data-table__sub-table * {
    /* This is to put the sub-table behind the main table's th */
    z-index: 9 !important;
}

.data-table__sub-table {
    table-layout: auto;
    width: 100%;
}

.data-table a {
    font-family: Roboto !important;
}

.data-table button {
    border: 1px solid rgb(118, 118, 118, 0.75);
    color: rgb(37, 36, 34);
    border-radius: 2px;
    text-wrap: nowrap;
}

.data-table button:disabled {
    color: rgb(37, 36, 34, 0.25);
}

.data-table a:focus,
.data-table button:focus,
.data-table input:focus {
    outline: rgba(102, 179, 227, 0.75) auto 1px !important;
    outline-offset: 0px;
    z-index: 99;
}

.data-table__spinner {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgb(0, 0, 0, 0.25);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
}

.data-table__spinner-icon {
    width: 200px;
    height: 200px;
    border-radius: 50%;
    border: 10px solid rgb(0, 0, 0, 0.25);
    border-top-color: rgb(20, 59, 76);
    animation: spinning 1s linear infinite;
}

@keyframes spinning {
    to {
        transform: rotate(360deg);
    }
}

.data-table__sub-table-heading {
    text-align: center;
    width: 15%;
}

.data-table__column-header {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: start;
}

.data-table__column-header__label {
    flex-grow: 1;
    padding-top: 1px;
    padding-bottom: 1px;
    padding-left: 6px;
}

.data-table__header,
.data-table__footer {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    align-items: end;
    gap: 6px;
    margin-top: 6px;
    margin-bottom: 6px;
}

.data-table__header-section {
    display: flex;
    align-items: end;
    gap: 6px;
}

.data-table__refresh {
    display: flex;
    gap: 6px;
    flex-flow: row wrap;
    white-space: nowrap;
}

.data-table__grow {
    flex-grow: 1;
}

.data-table__search {
    display: flex;
    flex-grow: 0.5;
    border: 1px solid rgb(118, 118, 118);
    border-radius: 2px;
    max-width: 450px;
}

.data-table__search input {
    border: none;
}

.data-table__search button {
    border: none;
}

.data-table__search button:first-of-type {
    border-left: 1px solid rgb(118, 118, 118);
    border-right: 1px solid rgb(118, 118, 118);
    border-radius: 0;
}

.data-table__refresh-spinner {
    margin: auto;
}

.data-table__no-data {
    text-align: center;
    line-height: 5;
}

.data-table__cell-select {
    width: 24px;
    text-align: center;
}

.data-table__cell-text,
.data-table__cell-category {
    padding-left: 6px;
    padding-right: 6px;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.data-table__cell-number,
.data-table__cell-datetime {
    padding-left: 6px;
    padding-right: 6px;
    text-align: right;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.data-table__cell-boolean {
    width: 48px;
    padding-left: 6px;
    padding-right: 6px;
    text-align: center;
    font-size: larger;
}

.data-table__cell-table {
    padding-left: 6px;
    padding-right: 6px;
    text-align: center;
    margin: auto;
}

.data-table__cell-action {
    padding-left: 6px;
    padding-right: 6px;
    text-align: center;
    margin: auto;
}

.data-table_cell-input {
    font-family: 'Roboto';
    font-size: 14px;
    height: 28px;
    padding-left: 3px;
    padding-right: 2px;
}

.data-table_cell-input input:focus {
    outline: rgba(102, 179, 227, 0.75) auto 1px !important;
    outline-offset: 0px;
    z-index: 99;
}

.subdued {
    opacity: 0.5;
    transition: opacity 2.0s ease-in-out;
}
</style>
